import React, { memo, useCallback, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Collapse,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FormatListNumbered as SubPartsIcon,
  ImageOutlined as ImageIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  QuestionFormData,
  QuestionType,
  QuestionImportance,
} from './types';
import { QuestionDifficulty, questionPaperService } from '../../services/questionPaperService';
import MathInput from '../shared/MathInput';
import { elegantMenuProps } from '../../utils/menuStyles';
import { toUploadUrl } from '../../utils/uploadUrl';



const toRoman = (num: number): string => {
  const romanNumerals: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']
  ];
  let result = '';
  for (const [value, numeral] of romanNumerals) {
    while (num >= value) { result += numeral; num -= value; }
  }
  return result.toLowerCase();
};

interface QuestionDialogProps {
  open: boolean;
  isEdit: boolean;
  formData: QuestionFormData;
  tagsInput: string;
  pastPapersInput: string;
  onClose: () => void;
  onFormChange: (data: QuestionFormData) => void;
  onTagsInputChange: (value: string) => void;
  onPastPapersInputChange: (value: string) => void;
  onSubmit: () => void;
}

const QuestionDialog = memo<QuestionDialogProps>(({
  open,
  isEdit,
  formData,
  tagsInput,
  pastPapersInput,
  onClose,
  onFormChange,
  onTagsInputChange,
  onPastPapersInputChange,
  onSubmit,
}) => {
  const handleClose = () => {
    onClose();
  };

  const handleAddOption = () => {
    onFormChange({
      ...formData,
      options: [...(formData.options || []), ''],
    });
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = formData.options?.filter((_, i) => i !== index) || [];
    onFormChange({ 
      ...formData, 
      options: newOptions,
      correctAnswer: newOptions.includes(formData.correctAnswer || '') 
        ? formData.correctAnswer 
        : ''
    });
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...(formData.options || [])];
    newOptions[index] = value;
    onFormChange({ ...formData, options: newOptions });
  };

  const handleTagsChange = (value: string) => {
    onTagsInputChange(value);
    onFormChange({
      ...formData,
      metadata: {
        ...formData.metadata,
        tags: value ? value.split(',').map((t) => t.trim()).filter((t) => t) : [],
      },
    });
  };

  const handlePastPapersChange = (value: string) => {
    onPastPapersInputChange(value);
    onFormChange({
      ...formData,
      metadata: {
        ...formData.metadata,
        pastPaperReferences: value ? value.split(',').map((r) => r.trim()).filter((r) => r) : [],
      },
    });
  };

  // ── Sample-answer image attachments ───────────────────────────────────────
  const { enqueueSnackbar } = useSnackbar();
  const answerImageInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAnswerImages, setUploadingAnswerImages] = useState(false);

  const handleAnswerImagesPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Reset input so re-picking the same file still fires change.
    if (answerImageInputRef.current) answerImageInputRef.current.value = '';
    if (files.length === 0) return;
    const existing = formData.answerImages || [];
    if (existing.length + files.length > 10) {
      enqueueSnackbar('You can attach at most 10 images per answer.', { variant: 'warning' });
      return;
    }
    setUploadingAnswerImages(true);
    try {
      const filenames = await questionPaperService.uploadAnswerImages(files);
      onFormChange({
        ...formData,
        answerImages: [...existing, ...filenames],
      });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to upload images', { variant: 'error' });
    } finally {
      setUploadingAnswerImages(false);
    }
  };

  const handleRemoveAnswerImage = (filename: string) => {
    onFormChange({
      ...formData,
      answerImages: (formData.answerImages || []).filter((f) => f !== filename),
    });
  };

  // ── Question-stem image attachments ───────────────────────────────────────
  const questionImageInputRef = useRef<HTMLInputElement>(null);
  const [uploadingQuestionImages, setUploadingQuestionImages] = useState(false);

  const handleQuestionImagesPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (questionImageInputRef.current) questionImageInputRef.current.value = '';
    if (files.length === 0) return;
    const existing = formData.questionImages || [];
    if (existing.length + files.length > 10) {
      enqueueSnackbar('You can attach at most 10 images per question.', { variant: 'warning' });
      return;
    }
    setUploadingQuestionImages(true);
    try {
      const filenames = await questionPaperService.uploadQuestionImages(files);
      onFormChange({
        ...formData,
        questionImages: [...existing, ...filenames],
      });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to upload images', { variant: 'error' });
    } finally {
      setUploadingQuestionImages(false);
    }
  };

  const handleRemoveQuestionImage = (filename: string) => {
    onFormChange({
      ...formData,
      questionImages: (formData.questionImages || []).filter((f) => f !== filename),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onKeyDown={(e) => e.stopPropagation()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{isEdit ? 'Edit Question' : 'Create Question'}</DialogTitle>
      <DialogContent
        onKeyDown={(e) => e.stopPropagation()}
        onKeyPress={(e) => e.stopPropagation()}
      >
        <Box sx={{ pt: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Type *</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={formData.type}
                  label="Type *"
                  onChange={(e) =>
                    onFormChange({
                      ...formData,
                      type: e.target.value as QuestionType,
                    })
                  }
                >
                  {Object.values(QuestionType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Importance</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={formData.importance}
                  label="Importance"
                  onChange={(e) =>
                    onFormChange({
                      ...formData,
                      importance: e.target.value as QuestionImportance,
                    })
                  }
                >
                  {Object.values(QuestionImportance).map((imp) => (
                    <MenuItem key={imp} value={imp}>
                      {imp}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <MathInput
            fullWidth
            label="Question Text"
            required
            multiline
            rows={3}
            value={formData.questionText}
            onChange={(val) =>
              onFormChange({ ...formData, questionText: val })
            }
            sx={{ mb: 1 }}
          />

          {/* Question-stem image attachments — smaller than answer images on
              purpose since they're supporting figures. */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={uploadingQuestionImages ? <CircularProgress size={14} /> : <ImageIcon fontSize="small" />}
                disabled={uploadingQuestionImages || (formData.questionImages?.length ?? 0) >= 10}
                onClick={() => questionImageInputRef.current?.click()}
                sx={{ textTransform: 'none' }}
              >
                {uploadingQuestionImages ? 'Uploading…' : 'Attach images to question'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {(formData.questionImages?.length ?? 0)}/10 attached · images only, max 5 MB each
              </Typography>
            </Box>
            <input
              ref={questionImageInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleQuestionImagesPick}
            />
            {(formData.questionImages?.length ?? 0) > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(formData.questionImages || []).map((filename) => (
                  <Box
                    key={filename}
                    sx={{
                      position: 'relative',
                      width: 72,
                      height: 72,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      component="img"
                      src={toUploadUrl('question-images', filename)}
                      alt="Question attachment"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveQuestionImage(filename)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        width: 18,
                        height: 18,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* MCQ Options */}
          {formData.type === QuestionType.MCQ && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Options* (Minimum 2 required)
              </Typography>
              {formData.options?.map((option, index) => (
                <Box key={index} display="flex" gap={1} mb={1}>
                  <MathInput
                    fullWidth
                    label={`Option ${index + 1}`}
                    value={option}
                    onChange={(val) => handleOptionChange(index, val)}
                    required={index < 2}
                    multiline={false}
                    size="small"
                  />
                  {(formData.options?.length || 0) > 2 && (
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddOption}
                sx={{ mb: 2 }}
              >
                Add Option
              </Button>
              <FormControl fullWidth>
                <InputLabel>Correct Answer (Optional)</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={formData.correctAnswer}
                  label="Correct Answer (Optional)"
                  onChange={(e) =>
                    onFormChange({ ...formData, correctAnswer: e.target.value })
                  }
                >
                  <MenuItem value="">Select Correct Answer</MenuItem>
                  {formData.options?.filter(opt => opt.trim()).map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Fill in the Blanks */}
          {formData.type === QuestionType.FILL_IN_THE_BLANKS && (
            <MathInput
              fullWidth
              label="Correct Answer (Optional)"
              value={formData.correctAnswer || ''}
              onChange={(val) =>
                onFormChange({ ...formData, correctAnswer: val })
              }
              multiline={false}
              sx={{ mt: 2 }}
            />
          )}

          {/* Short/Long Answer */}
          {(formData.type === QuestionType.SHORT ||
            formData.type === QuestionType.LONG) && (
            <>
              

              {/* Sub-parts Section */}
              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  p: 2,
                  borderStyle: 'dashed',
                  borderColor: formData.subParts && formData.subParts.length > 0 ? 'primary.main' : 'divider',
                  bgcolor: formData.subParts && formData.subParts.length > 0 ? 'primary.50' : 'transparent',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={formData.subParts && formData.subParts.length > 0 ? 1.5 : 0}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SubPartsIcon fontSize="small" color={formData.subParts && formData.subParts.length > 0 ? 'primary' : 'action'} />
                    <Typography variant="subtitle2" color={formData.subParts && formData.subParts.length > 0 ? 'primary' : 'text.secondary'}>
                      Sub-parts {formData.subParts && formData.subParts.length > 0 ? `(${formData.subParts.length})` : '(Optional)'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      onFormChange({
                        ...formData,
                        subParts: [...(formData.subParts || []), { text: '' }],
                      });
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Add Part
                  </Button>
                </Box>

                <Collapse in={!!(formData.subParts && formData.subParts.length > 0)}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {formData.subParts?.map((part, index) => (
                      <Box key={index} display="flex" gap={1} alignItems="flex-start">
                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: 32,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            color: 'primary.main',
                            bgcolor: 'primary.50',
                            borderRadius: 1,
                            fontSize: '0.8rem',
                          }}
                        >
                          ({toRoman(index + 1)})
                        </Typography>
                        <MathInput
                          fullWidth
                          size="small"
                          placeholder={`Sub-part ${toRoman(index + 1)}`}
                          value={part.text}
                          onChange={(val) => {
                            const newParts = [...(formData.subParts || [])];
                            newParts[index] = { text: val };
                            onFormChange({ ...formData, subParts: newParts });
                          }}
                          multiline={false}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const newParts = (formData.subParts || []).filter((_, i) => i !== index);
                            onFormChange({ ...formData, subParts: newParts.length > 0 ? newParts : undefined });
                          }}
                          sx={{ mt: 0.25 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Paper>
              <MathInput
                fullWidth
                label="Sample Answer (Optional)"
                multiline
                rows={formData.type === QuestionType.LONG ? 4 : 2}
                value={formData.answer || ''}
                onChange={(val) =>
                  onFormChange({ ...formData, answer: val })
                }
                sx={{ mt: 2 }}
              />

              {/* Sample-answer image attachments — up to 10 per question. */}
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={uploadingAnswerImages ? <CircularProgress size={14} /> : <ImageIcon fontSize="small" />}
                    disabled={uploadingAnswerImages || (formData.answerImages?.length ?? 0) >= 10}
                    onClick={() => answerImageInputRef.current?.click()}
                    sx={{ textTransform: 'none' }}
                  >
                    {uploadingAnswerImages ? 'Uploading…' : 'Attach images to answer'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {(formData.answerImages?.length ?? 0)}/10 attached · images only, max 5 MB each
                  </Typography>
                </Box>
                <input
                  ref={answerImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handleAnswerImagesPick}
                />
                {(formData.answerImages?.length ?? 0) > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(formData.answerImages || []).map((filename) => (
                      <Box
                        key={filename}
                        sx={{
                          position: 'relative',
                          width: 96,
                          height: 96,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box
                          component="img"
                          src={toUploadUrl('answer-images', filename)}
                          alt="Answer attachment"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveAnswerImage(filename)}
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            width: 20,
                            height: 20,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Metadata */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Metadata (Optional)
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Marks"
                value={formData.metadata?.marks || ''}
                onChange={(e) =>
                  onFormChange({
                    ...formData,
                    metadata: {
                      ...formData.metadata,
                      marks: e.target.value ? parseInt(e.target.value) : undefined,
                    },
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select MenuProps={elegantMenuProps}
                  value={formData.metadata?.difficulty || QuestionDifficulty.EASY}
                  label="Difficulty"
                  onChange={(e) =>
                    onFormChange({
                      ...formData,
                      metadata: { ...formData.metadata, difficulty: e.target.value },
                    })
                  }
                >
                  {Object.values(QuestionDifficulty).map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Time (minutes)"
                value={formData.metadata?.estimatedTime || ''}
                onChange={(e) =>
                  onFormChange({
                    ...formData,
                    metadata: {
                      ...formData.metadata,
                      estimatedTime: e.target.value ? parseFloat(e.target.value) : undefined,
                    },
                  })
                }
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={tagsInput}
                onChange={(e) => handleTagsChange(e.target.value)}
                onKeyPress={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                helperText="e.g., algebra, equations, linear"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Past Paper References (comma-separated)"
                value={pastPapersInput}
                onChange={(e) => handlePastPapersChange(e.target.value)}
                onKeyPress={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                helperText="e.g., 2023, 2022"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSubmit}>
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

QuestionDialog.displayName = 'QuestionDialog';

export default QuestionDialog;
